import { CheckIcon } from '@heroicons/react/24/solid';
import guaraniTooltips from '../../config/guaraniTooltips';

/**
 * Category definitions with display metadata.
 * Each entry maps a category value to its label, short description, accent color, and tooltip key.
 */
export const GUARANI_CATEGORIES = [
  {
    value: 'guarani_estructura_guarani',
    label: 'Guaraní c/ Estructura Guaraní',
    shortDesc: 'Morfología y sintaxis guaraní',
    color: {
      border: 'border-indigo-500',
      bg: 'bg-indigo-50',
      hoverBorder: 'hover:border-indigo-300',
      hoverBg: 'hover:bg-indigo-50',
      titleActive: 'text-indigo-900',
      subtitleActive: 'text-indigo-700',
      bodyActive: 'text-indigo-600',
      check: 'bg-indigo-500',
      badge: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    },
    tooltipKey: 'guarani_estructura_guarani',
  },
  {
    value: 'guarani_estructura_castellano',
    label: 'Guaraní c/ Estructura Castellano',
    shortDesc: 'Sintaxis adaptada al español',
    color: {
      border: 'border-teal-500',
      bg: 'bg-teal-50',
      hoverBorder: 'hover:border-teal-300',
      hoverBg: 'hover:bg-teal-50',
      titleActive: 'text-teal-900',
      subtitleActive: 'text-teal-700',
      bodyActive: 'text-teal-600',
      check: 'bg-teal-500',
      badge: 'bg-teal-100 text-teal-800 border-teal-300',
    },
    tooltipKey: 'guarani_estructura_castellano',
  },
  {
    value: 'jopara',
    label: 'Jopara',
    shortDesc: 'Cotidiano con hibridación',
    color: {
      border: 'border-green-500',
      bg: 'bg-green-50',
      hoverBorder: 'hover:border-green-300',
      hoverBg: 'hover:bg-green-50',
      titleActive: 'text-green-900',
      subtitleActive: 'text-green-700',
      bodyActive: 'text-green-600',
      check: 'bg-green-500',
      badge: 'bg-green-100 text-green-800 border-green-300',
    },
    tooltipKey: 'jopara',
  },
  {
    value: 'jehea',
    label: "Jehe'a",
    shortDesc: 'Mezcla léxica GN-ES',
    color: {
      border: 'border-orange-500',
      bg: 'bg-orange-50',
      hoverBorder: 'hover:border-orange-300',
      hoverBg: 'hover:bg-orange-50',
      titleActive: 'text-orange-900',
      subtitleActive: 'text-orange-700',
      bodyActive: 'text-orange-600',
      check: 'bg-orange-500',
      badge: 'bg-orange-100 text-orange-800 border-orange-300',
    },
    tooltipKey: 'jehea',
  },
];

/** Returns the display label for a given category value, or the value itself as fallback. */
export const getCategoryLabel = (value) =>
  GUARANI_CATEGORIES.find((c) => c.value === value)?.label ?? value;

/**
 * GuaraniCategorySelector
 *
 * Props:
 *   selectedCategory {string|null}  - current selected value
 *   onChange         {Function}     - called with the value (toggled off if same)
 *   variant          {'full'|'compact'} - 'full' shows cards with tooltips; 'compact' shows small pills
 *   tooltips         {object}       - optional override for tooltip data (defaults to guaraniTooltips)
 *   disabled         {boolean}
 */
const GuaraniCategorySelector = ({
  selectedCategory,
  onChange,
  variant = 'full',
  tooltips = guaraniTooltips,
  disabled = false,
}) => {
  const handleClick = (value) => {
    if (disabled) return;
    onChange(selectedCategory === value ? null : value);
  };

  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap gap-2">
        {GUARANI_CATEGORIES.map((cat) => {
          const isSelected = selectedCategory === cat.value;
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => handleClick(cat.value)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                isSelected
                  ? 'bg-indigo-500 border-indigo-500 text-white font-medium shadow-sm'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <span className="font-medium">{cat.label}</span>
              <span className="ml-1.5 text-xs opacity-75">{cat.shortDesc}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // variant === 'full'
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {GUARANI_CATEGORIES.map((cat) => {
          const isSelected = selectedCategory === cat.value;
          const tip = tooltips[cat.tooltipKey] ?? {};
          const c = cat.color;
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => handleClick(cat.value)}
              disabled={disabled}
              className={`group relative p-4 border-2 rounded-lg transition-all duration-200 text-left ${
                isSelected
                  ? `${c.border} ${c.bg} shadow-md`
                  : `border-gray-200 bg-white ${c.hoverBorder} ${c.hoverBg} hover:shadow-sm`
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className={`text-sm font-semibold mb-1 ${isSelected ? c.titleActive : 'text-gray-800'}`}>
                    {cat.label}
                  </div>
                  <div className={`text-xs ${isSelected ? c.subtitleActive : 'text-gray-600'}`}>
                    {cat.shortDesc}
                  </div>
                </div>
                {isSelected && (
                  <div className="flex-shrink-0 ml-2">
                    <div className={`w-6 h-6 ${c.check} rounded-full flex items-center justify-center`}>
                      <CheckIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}
              </div>

              {tip.desc && (
                <div className={`text-xs leading-relaxed line-clamp-2 ${isSelected ? c.bodyActive : 'text-gray-500'}`}>
                  {tip.desc}
                </div>
              )}

              {/* Tooltip on hover */}
              {(tip.title || tip.desc) && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-72 z-10 pointer-events-none">
                  {tip.title && <div className="font-semibold mb-1">{tip.title}</div>}
                  {tip.desc && <div className="text-gray-200 mb-1">{tip.desc}</div>}
                  {tip.example && (
                    <div className="text-gray-300 italic text-[10px] mt-1">{tip.example}</div>
                  )}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedCategory && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span className="text-gray-700">
              Categoría seleccionada:{' '}
              <span className="font-semibold text-gray-900">{getCategoryLabel(selectedCategory)}</span>
            </span>
            <span className="text-gray-400 text-xs ml-auto">Haz clic de nuevo para deseleccionar</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuaraniCategorySelector;
